import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Box, Typography, IconButton, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Button, InputAdornment, Tooltip, Autocomplete, Chip } from '@mui/material';
import { useTranslation } from 'react-i18next';
import LocalPhoneIcon from '@mui/icons-material/LocalPhone';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import AddIcon from '@mui/icons-material/Add';
import MenuBookIcon from '@mui/icons-material/MenuBook';
import CloseIcon from '@mui/icons-material/Close';
import SettingsIcon from '@mui/icons-material/Settings';
import DescriptionOutlinedIcon from '@mui/icons-material/DescriptionOutlined';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';
import PhoneNumbersSettingsDialog from '../../modals/PhoneNumbersSettingsDialog';
import PhoneCatalogBrowser, { convertToUSD } from './PhoneCatalogBrowser';
import DocsNotePopovers from './DocsNotePopovers';
import PhoneNumberAmountInput from './PhoneNumberAmountInput';
import { useCalculator } from '../../../../contexts/CalculatorContext';
import { usePricing } from '../../../../contexts/PricingContext';
import { useUserPreferences } from '../../../../contexts/UserPreferencesContext';
import { getPhoneCatalog, getPhoneCatalogEntry } from '../../../../services/phoneCatalogApi';
import {
  convertUSDToDisplay,
  getCurrencySymbolByCode,
  getEffectiveDisplayCurrency,
  roundDisplayAmountForCurrency,
} from '../../../../utils/currencyUtils';

const PhoneNumbers = ({
  phoneNumbers,
  setPhoneNumbers,
  currency, // legacy symbol prop, retained for backward compat but no longer used for display
  id,
  toggleProduct
}) => {
  const { t } = useTranslation();
  const {
    phoneNumbersSettingsOpen,
    setPhoneNumbersSettingsOpen,
    enablePhoneActivation,
    hasPhoneNumbersCustomSettings,
    handleApplyPhoneNumbersSettings
  } = useCalculator();
  const { currencyRates } = usePricing();
  const { region, displayCurrency } = useUserPreferences();
  const effectiveCurrency = getEffectiveDisplayCurrency(region, displayCurrency);
  const effectiveSymbol = getCurrencySymbolByCode(effectiveCurrency);

  const [catalogOpen, setCatalogOpen] = useState(false);
  const [docsPopover, setDocsPopover] = useState({ anchorEl: null, text: '' });
  const [notePopover, setNotePopover] = useState({ anchorEl: null, text: '' });

  const catalogRefreshKey = useMemo(() => {
    return (phoneNumbers || [])
      .filter(phone => phone.catalogId)
      .map(phone => `${phone.id}:${phone.catalogId}`)
      .sort()
      .join('|');
  }, [phoneNumbers]);

  useEffect(() => {
    if (!catalogRefreshKey || !currencyRates?.usd) return;

    let cancelled = false;

    const refreshCatalogPrices = async () => {
      const catalogIds = [
        ...new Set(catalogRefreshKey.split('|').map(value => value.split(':')[1]).filter(Boolean)),
      ];

      try {
        const records = await Promise.all(
          catalogIds.map(async (catalogId) => {
            try {
              const response = await getPhoneCatalogEntry(catalogId);
              return [catalogId, response.data?.data];
            } catch {
              return [catalogId, null];
            }
          })
        );

        if (cancelled) return;

        const recordById = new Map(records.filter(([, record]) => record));
        setPhoneNumbers(prev => {
          let changed = false;

          const next = prev.map(phone => {
            const record = recordById.get(phone.catalogId);
            if (!record) return phone;

            const nextPrice = convertToUSD(record.monthly_fee, record.currency, currencyRates);
            const nextActivationFee = convertToUSD(record.activation_fee, record.currency, currencyRates) || 0;

            const updates = {
              price: nextPrice ?? phone.price,
              activationFee: nextActivationFee,
              catalogCurrency: record.currency,
              isFromPrice: record.is_from_price || false,
              comment: record.comment || '',
              requiredDocuments: record.required_documents || '',
              isFreeForTrial: record.is_free_for_trial || false,
            };

            const hasChanged = Object.entries(updates).some(([key, value]) => phone[key] !== value);
            if (!hasChanged) return phone;

            changed = true;
            return { ...phone, ...updates };
          });

          return changed ? next : prev;
        });
      } catch (err) {
        if (!cancelled) {
          console.error('Failed to refresh phone catalog prices', err);
        }
      }
    };

    refreshCatalogPrices();

    return () => {
      cancelled = true;
    };
  }, [catalogRefreshKey, currencyRates, setPhoneNumbers]);

  // Autocomplete state per row
  const [autocompleteOptions, setAutocompleteOptions] = useState({});
  const [autocompleteLoading, setAutocompleteLoading] = useState({});
  const debounceTimers = useRef({});

  // Format a USD amount in the user's effective display currency.
  // { ceil: true } applies the same per-currency rounding as Cena jednostkowa
  // (0.1 step for EUR/USD, 1 unit for UAH/KZT) so unit and total stay
  // consistent — previously this used Math.ceil to integer, which made
  // 1 × €3.1 render as €4.
  const formatMoney = (usdAmount, { ceil = false } = {}) => {
    const usd = Number(usdAmount || 0);
    const display = Number(convertUSDToDisplay(usd, effectiveCurrency, currencyRates) || 0);
    const rounded = ceil ? roundDisplayAmountForCurrency(display, effectiveCurrency) : display;
    try {
      const formatted = new Intl.NumberFormat(undefined, {
        minimumFractionDigits: 0,
        maximumFractionDigits: 2
      }).format(rounded);
      return `${effectiveSymbol}${formatted}`;
    } catch {
      const safe = rounded.toFixed(2).replace(/\.00$/, '');
      return `${effectiveSymbol}${safe}`;
    }
  };

  const nameCounts = React.useMemo(() => {
    const map = {};
    (phoneNumbers || []).forEach(p => {
      const key = (p.name || '').trim().toLowerCase();
      if (!key) return;
      map[key] = (map[key] || 0) + 1;
    });
    return map;
  }, [phoneNumbers]);

  const isDuplicateName = (name) => {
    const key = (name || '').trim().toLowerCase();
    if (!key) return false;
    return (nameCounts[key] || 0) > 1;
  };

  const makeUniqueName = (baseName, currentId) => {
    let base = (baseName || '').trim();
    if (!base) return baseName;
    const existing = new Set(
      (phoneNumbers || [])
        .filter(p => p.id !== currentId)
        .map(p => (p.name || '').trim().toLowerCase())
    );
    if (!existing.has(base.toLowerCase())) return base;
    let i = 2;
    let candidate = '';
    do {
      candidate = `${base} (${i})`;
      i += 1;
    } while (existing.has(candidate.toLowerCase()));
    return candidate;
  };

  const handleAddPhoneNumber = () => {
    setPhoneNumbers([
      ...phoneNumbers,
      {
        id: Date.now(),
        name: '',
        quantity: 1,
        price: 0,
        activationFee: 0,
      },
    ]);
  };

  const handleEditPhoneNumber = (id, field, value) => {
    setPhoneNumbers(phoneNumbers.map(phone =>
      phone.id === id ? { ...phone, [field]: value } : phone
    ));
  };

  const clearCatalogLink = (id) => {
    setPhoneNumbers(phoneNumbers.map(phone =>
      phone.id === id
        ? {
            ...phone,
            name: '',
            catalogId: undefined,
            catalogCurrency: undefined,
            isFromPrice: false,
            comment: '',
            requiredDocuments: '',
            isFreeForTrial: false,
          }
        : phone
    ));
  };

  const handleDeletePhoneNumber = id => {
    setPhoneNumbers(phoneNumbers.filter(phone => phone.id !== id));
  };

  // Catalog browser: add selected entries
  const handleAddFromCatalog = useCallback((entries) => {
    const newPhoneNumbers = entries.map((entry, i) => ({
      id: Date.now() + i,
      name: entry.name,
      quantity: entry.quantity,
      price: entry.price,
      activationFee: entry.activationFee || 0,
      catalogId: entry.catalogId,
      catalogCurrency: entry.catalogCurrency,
      isFromPrice: entry.isFromPrice,
      comment: entry.comment || '',
      requiredDocuments: entry.requiredDocuments || '',
      isFreeForTrial: entry.isFreeForTrial || false,
    }));
    setPhoneNumbers(prev => [...prev, ...newPhoneNumbers]);
    setCatalogOpen(false);
  }, [setPhoneNumbers]);

  // Autocomplete: search catalog
  const handleNameSearch = useCallback((phoneId, query) => {
    if (debounceTimers.current[phoneId]) clearTimeout(debounceTimers.current[phoneId]);

    if (!query || query.length < 2) {
      setAutocompleteOptions(prev => ({ ...prev, [phoneId]: [] }));
      return;
    }

    setAutocompleteLoading(prev => ({ ...prev, [phoneId]: true }));
    debounceTimers.current[phoneId] = setTimeout(async () => {
      try {
        const res = await getPhoneCatalog({ search: query, limit: 10 });
        const data = res.data?.data || [];
        setAutocompleteOptions(prev => ({ ...prev, [phoneId]: data }));
      } catch {
        setAutocompleteOptions(prev => ({ ...prev, [phoneId]: [] }));
      } finally {
        setAutocompleteLoading(prev => ({ ...prev, [phoneId]: false }));
      }
    }, 300);
  }, []);

  // Autocomplete: select a catalog entry
  const handleSelectCatalogEntry = useCallback((phoneId, entry) => {
    if (!entry || typeof entry === 'string') return;

    const parts = [entry.country];
    if (entry.city) parts.push(entry.city);
    const name = entry.provider ? `${parts.join(', ')} — ${entry.provider}` : parts.join(', ');

    setPhoneNumbers(prev => prev.map(phone => {
      if (phone.id !== phoneId) return phone;
      return {
        ...phone,
        name,
        price: convertToUSD(entry.monthly_fee, entry.currency, currencyRates) || 0,
        activationFee: convertToUSD(entry.activation_fee, entry.currency, currencyRates) || 0,
        catalogId: entry._id || entry.id,
        catalogCurrency: entry.currency,
        isFromPrice: entry.is_from_price || false,
        comment: entry.comment || '',
        requiredDocuments: entry.required_documents || '',
        isFreeForTrial: entry.is_free_for_trial || false,
      };
    }));
  }, [setPhoneNumbers, currencyRates]);

  return (
    <Box>
        {/* Header */}
        <Box sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 1
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <LocalPhoneIcon sx={{ color: '#333', fontSize: 20, mr: 1 }} />
            <Typography sx={{ fontWeight: 500 }}>
              {t('calculator.phoneNumbers', 'Phone Numbers')}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <IconButton
              onClick={() => setPhoneNumbersSettingsOpen(true)}
              size="small"
              sx={{
                color: hasPhoneNumbersCustomSettings ? '#00C853' : 'text.secondary',
                '&:hover': { color: 'primary.main', backgroundColor: 'action.hover' },
                p: 0.5
              }}
            >
              <SettingsIcon fontSize="small" />
            </IconButton>
            <IconButton
              size="small"
              onClick={() => toggleProduct(id)}
              sx={{ color: '#333', '&:hover': { color: '#00C853' } }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>

        {/* Table Container */}
        <TableContainer sx={{
          border: '1px solid #eee',
          borderRadius: 1,
          mb: 2,
          overflow: 'hidden'
        }}>
          <Table size="small" sx={{
            minWidth: 300,
            tableLayout: 'fixed',
            '& .MuiTableCell-root': {
              borderColor: '#eee',
              py: 1.5,
              px: { xs: 1, sm: 2 }
            }
          }}>
            {enablePhoneActivation ? (
              <colgroup>
                <col style={{ width: '36%' }} />
                <col style={{ width: '9%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '10%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '19%' }} />
              </colgroup>
            ) : (
              <colgroup>
                <col style={{ width: '46%' }} />
                <col style={{ width: '14%' }} />
                <col style={{ width: '16%' }} />
                <col style={{ width: '24%' }} />
              </colgroup>
            )}
            <TableHead>
              <TableRow sx={{
                bgcolor: 'white',
                '& .MuiTableCell-root': {
                  fontWeight: 500,
                  color: 'rgba(0,0,0,0.6)',
                  fontSize: 13,
                  borderBottom: '1px solid #eee'
                }
              }}>
                <TableCell align="left">{t('calculator.phoneNumberName', 'NAME')}</TableCell>
                <TableCell align="center">{t('calculator.quantity', 'QUANTITY')}</TableCell>
                <TableCell align="center">{t('calculator.unitPrice', 'UNIT PRICE')}</TableCell>
                {enablePhoneActivation && (
                  <>
                    <TableCell align="center">{t('calculator.activationFee', 'ACTIVATION')}</TableCell>
                    <TableCell align="right">{t('calculator.activationTotal', 'ACTIVATION TOTAL')}</TableCell>
                  </>
                )}
                <TableCell align="right">{t('calculator.totalPerMonth', 'TOTAL PER MONTH')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {phoneNumbers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={enablePhoneActivation ? 6 : 4} sx={{ textAlign: 'center', color: 'rgba(0,0,0,0.45)' }}>
                    {t('calculator.noPhoneNumbers', 'No phone numbers added yet. Click "Add phone number" to add one.')}
                  </TableCell>
                </TableRow>
              ) : (
                phoneNumbers.map((phone) => (
                  <TableRow
                    key={phone.id}
                    sx={{
                      bgcolor: 'transparent',
                      '&:last-child .MuiTableCell-root': { borderBottom: 'none' }
                    }}
                  >
                    {/* Name Cell with Autocomplete */}
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <LocalPhoneIcon sx={{ fontSize: 18, color: phone.catalogId ? '#1976d2' : 'rgba(0,200,83,0.7)', mr: 1, flexShrink: 0 }} />
                        <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                          <Autocomplete
                            freeSolo
                            size="small"
                            options={autocompleteOptions[phone.id] || []}
                            loading={autocompleteLoading[phone.id] || false}
                            inputValue={phone.name || ''}
                            onInputChange={(_, value, reason) => {
                              if (reason === 'input') {
                                handleEditPhoneNumber(phone.id, 'name', value);
                                handleNameSearch(phone.id, value);
                              } else if (reason === 'clear') {
                                clearCatalogLink(phone.id);
                              }
                            }}
                            onChange={(_, value) => {
                              if (value && typeof value === 'object') {
                                handleSelectCatalogEntry(phone.id, value);
                              }
                            }}
                            onBlur={() => {
                              const unique = makeUniqueName(phone.name, phone.id);
                              if (unique !== phone.name) {
                                handleEditPhoneNumber(phone.id, 'name', unique);
                              }
                            }}
                            getOptionLabel={(option) => {
                              if (typeof option === 'string') return option;
                              const parts = [option.country];
                              if (option.city) parts.push(option.city);
                              return option.provider ? `${parts.join(', ')} — ${option.provider}` : parts.join(', ');
                            }}
                            filterOptions={(x) => x}
                            renderOption={(props, option) => {
                              const { key, ...restProps } = props;
                              const parts = [option.country];
                              if (option.city) parts.push(option.city);
                              const label = option.provider ? `${parts.join(', ')} — ${option.provider}` : parts.join(', ');
                              const sym = option.currency === 'EUR' ? '€' : option.currency === 'UAH' ? '₴' : '$';
                              return (
                                <Box
                                  component="li"
                                  key={key}
                                  {...restProps}
                                  sx={{ display: 'flex', justifyContent: 'space-between', width: '100%', fontSize: 13 }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, overflow: 'hidden' }}>
                                    <Typography noWrap sx={{ fontSize: 13 }}>{label}</Typography>
                                    {option.is_from_price && (
                                      <Chip label={t('calculator.fromPrice', 'FROM')} size="small" sx={{ height: 14, fontSize: 9, fontWeight: 700, bgcolor: '#FFF8E1', color: '#F57F17', borderRadius: 1 }} />
                                    )}
                                  </Box>
                                  <Typography sx={{ fontSize: 12, color: 'rgba(0,0,0,0.45)', ml: 1, flexShrink: 0 }}>
                                    {option.monthly_fee != null ? `${sym}${option.monthly_fee}/mo` : ''}
                                  </Typography>
                                </Box>
                              );
                            }}
                            sx={{
                              flexGrow: 1,
                              '& .MuiInputBase-root': {
                                py: 0,
                                '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' },
                              },
                              '& .MuiInputBase-input': {
                                py: 0.5,
                                fontWeight: 500,
                                height: 32,
                              },
                              '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                            }}
                            componentsProps={{
                              popper: { sx: { '& .MuiAutocomplete-listbox': { maxHeight: 200 } } },
                            }}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                variant="outlined"
                                placeholder={t('calculator.phoneNamePlaceholder', 'Type to search catalog...')}
                                InputProps={{
                                  ...params.InputProps,
                                  endAdornment: (
                                    <>
                                      {params.InputProps.endAdornment}
                                      {isDuplicateName(phone.name) && (
                                        <Tooltip title={t('calculator.duplicatePhoneNameShort', 'Name already used')} arrow>
                                          <InputAdornment position="end">
                                            <WarningAmberIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                                          </InputAdornment>
                                        </Tooltip>
                                      )}
                                      {phone.isFromPrice && (
                                        <Tooltip title={t('calculator.fromPriceHint', 'Price starts from this amount')} arrow>
                                          <Chip label={t('calculator.fromPrice', 'FROM')} size="small" sx={{ height: 16, fontSize: 9, fontWeight: 700, bgcolor: '#FFF8E1', color: '#F57F17', borderRadius: 1, ml: 0.5 }} />
                                        </Tooltip>
                                      )}
                                    </>
                                  ),
                                }}
                              />
                            )}
                          />
                        </Box>
                        {(phone.requiredDocuments || phone.comment) && (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25, ml: 0.5, flexShrink: 0 }}>
                            {phone.requiredDocuments && (
                              <Tooltip title={t('calculator.requiredDocs', 'Required documents')} arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => setDocsPopover({ anchorEl: e.currentTarget, text: phone.requiredDocuments })}
                                  sx={{ p: 0.3 }}
                                >
                                  <DescriptionOutlinedIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                            {phone.comment && (
                              <Tooltip title={t('calculator.commentTitle', 'Note')} arrow>
                                <IconButton
                                  size="small"
                                  onClick={(e) => setNotePopover({ anchorEl: e.currentTarget, text: phone.comment })}
                                  sx={{ p: 0.3 }}
                                >
                                  <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: '#1976d2' }} />
                                </IconButton>
                              </Tooltip>
                            )}
                          </Box>
                        )}
                      </Box>
                    </TableCell>

                    {/* Quantity Cell */}
                    <TableCell align="center">
                      <TextField
                        value={phone.quantity}
                        onChange={e => {
                          const newValue = e.target.value === '' ? '' : Number(e.target.value);
                          handleEditPhoneNumber(phone.id, 'quantity', newValue);
                        }}
                        onBlur={() => {
                          const value = phone.quantity;
                          if (value === '' || isNaN(value) || value < 1) {
                            handleEditPhoneNumber(phone.id, 'quantity', 1);
                          }
                        }}
                        type="number"
                        size="small"
                        variant="standard"
                        sx={{
                          width: 48,
                          '.MuiInputBase-input': { py: 0.5, textAlign: 'center', fontWeight: 500, height: 32, transition: 'background-color 0.2s' },
                          '.MuiInputBase-root': { '&:hover': { bgcolor: 'rgba(0, 0, 0, 0.02)' } }
                        }}
                        InputProps={{ disableUnderline: true, style: { textAlign: 'center' } }}
                      />
                    </TableCell>

                    {/* Unit Price Cell */}
                    <TableCell align="center">
                      <PhoneNumberAmountInput
                        usdValue={phone.price}
                        effectiveCurrency={effectiveCurrency}
                        currencyRates={currencyRates}
                        ceilDisplay
                        onCommit={(usdValue) => handleEditPhoneNumber(phone.id, 'price', usdValue)}
                      />
                    </TableCell>

                    {/* Activation Fee Cell */}
                    {enablePhoneActivation && (
                      <TableCell align="center">
                        <PhoneNumberAmountInput
                          usdValue={phone.activationFee}
                          effectiveCurrency={effectiveCurrency}
                          currencyRates={currencyRates}
                          onCommit={(usdValue) => handleEditPhoneNumber(phone.id, 'activationFee', usdValue)}
                        />
                      </TableCell>
                    )}

                    {/* Activation Total Cell */}
                    {enablePhoneActivation && (
                      <TableCell align="right">
                        <Typography variant="body1" sx={{ fontWeight: 600 }}>
                          {formatMoney(Number(phone.quantity || 0) * Number(phone.activationFee || 0))}
                        </Typography>
                      </TableCell>
                    )}

                    {/* Total (Monthly) Cell */}
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1 }}>
                        <Box sx={{ textAlign: 'right', fontWeight: 700, fontSize: 16 }}>
                          {formatMoney(Number(phone.quantity || 0) * Number(phone.price || 0), { ceil: true })}
                        </Box>
                        <IconButton
                          size="small"
                          onClick={() => handleDeletePhoneNumber(phone.id)}
                          sx={{ p: 0.5 }}
                        >
                          <CloseIcon sx={{ fontSize: '1rem' }} />
                        </IconButton>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Action Buttons */}
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', gap: 1, mt: 1 }}>
          <Button
            variant="text"
            startIcon={<MenuBookIcon />}
            onClick={() => setCatalogOpen(true)}
            sx={{
              textTransform: 'none',
              color: 'rgba(0,0,0,0.6)',
              '&:hover': { color: '#1976d2', backgroundColor: 'rgba(25,118,210,0.04)' }
            }}
          >
            {t('calculator.addFromCatalog', 'Add from Catalog')}
          </Button>
          <Button
            variant="text"
            startIcon={<AddIcon />}
            onClick={handleAddPhoneNumber}
            sx={{
              textTransform: 'none',
              color: 'rgba(0,0,0,0.6)',
              '&:hover': { color: '#00C853', backgroundColor: 'rgba(0,200,83,0.04)' }
            }}
          >
            {t('calculator.addPhoneNumber', 'Add phone number')}
          </Button>
        </Box>

        {/* Catalog Browser Modal */}
        <PhoneCatalogBrowser
          open={catalogOpen}
          onClose={() => setCatalogOpen(false)}
          onAddNumbers={handleAddFromCatalog}
          enablePhoneActivation={enablePhoneActivation}
        />

        {/* Phone Numbers Settings Dialog */}
        <PhoneNumbersSettingsDialog
          open={phoneNumbersSettingsOpen}
          onClose={() => setPhoneNumbersSettingsOpen(false)}
          onApply={handleApplyPhoneNumbersSettings}
          enablePhoneActivation={enablePhoneActivation}
          hasPhoneNumbersCustomSettings={hasPhoneNumbersCustomSettings}
        />

        <DocsNotePopovers
          docs={{
            anchorEl: docsPopover.anchorEl,
            text: docsPopover.text,
            onClose: () => setDocsPopover({ anchorEl: null, text: '' }),
          }}
          note={{
            anchorEl: notePopover.anchorEl,
            text: notePopover.text,
            onClose: () => setNotePopover({ anchorEl: null, text: '' }),
          }}
        />
    </Box>
  );
};

export default React.memo(PhoneNumbers);
